import { isOperatorCopy } from './operatorCopy';
import type { ListAudioInputsResult } from '../wire-corpus/generated';

/** One exact element of the controller's generated `list_audio_inputs` result. */
export type AudioInputDescriptor = NonNullable<ListAudioInputsResult['inputs']>[number];

/** The inputs an audio effect can start on: present, not a file, and carrying an identity. */
export function startableAudioInputs(result: ListAudioInputsResult | undefined): AudioInputDescriptor[] {
  return ((result?.inputs ?? []) as AudioInputDescriptor[])
    .filter((input) => input.available !== false && input.kind !== 'file' && Boolean(input.identity));
}

/** The room's chosen input among those, which both Start on Audio reactive and Play send. */
export function selectedStartableAudioInput(
  result: ListAudioInputsResult | undefined,
  identity: string,
): AudioInputDescriptor | undefined {
  return startableAudioInputs(result).find((input) => input.identity === identity);
}

/** One entry of the room's input picker: the identity it commits, and the words the operator reads. */
export interface AudioInputOption {
  identity: string;
  label: string;
}

export interface AudioInputPresentation {
  /** The complete operator-facing line used in the input picker. */
  label: string;
  /** The ALSA/PortAudio identity, shown below the picker when a friendlier product name exists. */
  rawName: string;
}

// The real Pi reports this device with TWO rooms — `USB AUDIO  CODEC: Audio (hw:2,0)` — so the run of
// whitespace is matched, not one room. A pattern that assumed one left the only interface installed at
// Alchemy unnamed on the very console built to name it.
const M_TRACK_DUO_ALSA = /^USB AUDIO\s+CODEC:\s*Audio\s*\(hw:\d+,\d+\)$/i;

/** The last thing an input can be called when nothing readable can be recovered from its record. */
const UNNAMED_INPUT = 'Audio input';

function clean(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function productName(input: AudioInputDescriptor, raw: string): string {
  const manufacturer = clean(input.usbManufacturer);
  const product = clean(input.usbProduct);
  if (product && !/^(usb audio codec|audio)$/i.test(product)) {
    if (!manufacturer || product.toLowerCase().includes(manufacturer.toLowerCase())) return product;
    return `${manufacturer} ${product}`;
  }
  // Older controllers know only the PortAudio/ALSA name. This exact device is the interface installed
  // at Alchemy, so the console can still name what the operator is looking at during the rollout.
  if (M_TRACK_DUO_ALSA.test(raw)) return 'M-Audio M-Track Duo';
  return clean(input.name) || clean(input.identity) || UNNAMED_INPUT;
}

function rateLabel(value: unknown): string {
  const rate = Number(value);
  if (!Number.isFinite(rate) || rate <= 0) return '';
  const khz = rate / 1_000;
  return `${Number.isInteger(khz) ? khz.toFixed(0) : khz.toFixed(1)} kHz`;
}

/** Turn the sidecar's device record into one human name while retaining its diagnostic ALSA name. */
export function audioInputPresentation(input: AudioInputDescriptor): AudioInputPresentation {
  const raw = clean(input.name) || clean(input.identity);
  const primary = productName(input, raw);
  const isUsb = Boolean(clean(input.usbVendorId) || clean(input.usbProductId))
    || /\busb\b/i.test(`${raw} ${input.usbManufacturer ?? ''} ${input.usbProduct ?? ''}`);
  const channels = Number(input.channels);
  const details = [
    isUsb ? 'USB' : '',
    Number.isFinite(channels) && channels > 0 ? `${Math.round(channels)} ch` : '',
    rateLabel(input.sampleRate),
    input.kind === 'synthetic' ? 'synthetic test signal' : '',
  ].filter(Boolean);
  return {
    label: [primary, ...details].join(' · '),
    rawName: raw && raw !== primary ? raw : '',
  };
}

/**
 * Whether one picker entry IS the input the controller reports as the room's.
 *
 * For a device the controller's published id is the entry's identity, byte for byte. For a synthetic
 * signal it is the CANONICAL id — the identity plus the parameters the signal is generated with, so
 * `synthetic:click_track` is published back as `synthetic:click_track:120bpm:0.7amp`. Those name one
 * input, and a console that demanded equality would blank its own picker the moment an operator chose a
 * test signal, which is the very confusion this screen exists to end. So an entry matches when the
 * reported id is its identity or extends it at a colon boundary.
 */
export function isRoomInput(identity: string, reportedId: string): boolean {
  const entry = clean(identity);
  const reported = clean(reportedId);
  if (!entry || !reported) return false;
  return reported === entry || reported.startsWith(`${entry}:`);
}

/**
 * The room's committed input as one picker entry, named the way a listed device would be.
 *
 * The controller publishes a label beside the id, and on a Pi that label is the sound card's ALSA name —
 * `USB AUDIO  CODEC: Audio (hw:2,0)` — which is an address, not a name an operator reads. So the label
 * is not shown verbatim: it goes through the same naming the picker applies to every enumerated device,
 * and only if that still cannot produce operator copy does the entry fall back to the generic name this
 * module already uses for an input it cannot name. The entry is never dropped. Dropping it is what put
 * an empty picker in front of an operator whose room was listening to a device the whole time.
 */
export function roomInputOption(
  room: { kind?: string; id?: string; label?: string } | undefined,
): AudioInputOption | undefined {
  const identity = clean(room?.id);
  if (!identity) return undefined;
  const label = audioInputPresentation({
    kind: clean(room?.kind) || 'device',
    identity,
    name: clean(room?.label) || identity,
    available: true,
  }).label;
  return { identity, label: isOperatorCopy(label) ? label : UNNAMED_INPUT };
}

/**
 * The picker's entries: every input the controller currently reports, plus the room's committed choice
 * whenever the controller does not list it. The room's choice must stay visible — a picker that silently
 * shows nothing selected is how an operator comes to believe the room has no music input while every
 * effect still follows one.
 *
 * The controller not listing the room's own input is not an exotic state. Controller 1.7.15 returns an
 * EMPTY inventory for as long as its room capture holds the device, so on the real Pi the committed
 * input is missing from the list exactly when a room has one.
 */
export function audioInputOptions(
  inputs: readonly AudioInputDescriptor[],
  room: { kind?: string; id?: string; label?: string } | undefined,
): AudioInputOption[] {
  const options = inputs.map((input) => ({
    identity: String(input.identity ?? ''),
    label: audioInputPresentation(input).label,
  }));
  const committed = String(room?.id ?? '');
  if (!committed || options.some((option) => isRoomInput(option.identity, committed))) return options;
  const entry = roomInputOption(room);
  return entry ? [entry, ...options] : options;
}
